/**
 * What people can do with a Fiona card, by tab (see fionaRules.js):
 * - Needs decision, open card: Accept (a packaging card asks for a stage and
 *   a check-in; a price card just confirms and closes) or Reject (a reason,
 *   and a note for the reasons that need one), each in an inline panel
 * - Needs decision with the check-in due: Update status (another stage,
 *   Resolved, or a new check-in date) or Reject
 * - In progress: the stages as steps, to move it forward, back or to
 *   Resolved, and Reject
 * - FYI (Resolved, i.e. finished): Mark as reviewed, which closes it
 * Only the store's owners can act, as on the other agents' cards.
 *
 * onCardChanged(change) tells the list what happened:
 * { fionaCard } with the card's new state, or { remove: "card" | "product" }
 * when it leaves the dashboard ("product": every Fiona card of the ASIN).
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import useCanRespondToDecisions from "../../../../../../hooks/useCanRespondToDecisions";
import { trackEvent } from "../../../../../../utils/analytics";
import { AGENT_INFO } from "../../../constants";

import {
  acceptFionaCard,
  fetchFionaOptions,
  markFionaCardReviewed,
  rejectFionaCard,
  updateFionaCardStatus,
} from "./fionaApi";
import {
  DEFAULT_FIONA_OPTIONS,
  DEFAULT_FOLLOW_UP,
  FOLLOW_UP_OPTIONS,
  STATUS_FINISHED,
  acceptStagesFor,
  cardTypeOf,
  datePart,
  followUpDate,
  formatFionaDate,
  getFionaTab,
  isAcceptedStage,
  isClosedOnAccept,
  isFollowUpDue,
  isGoneStatus,
  mergeFionaCard,
  rejectEffect,
  rejectHidesProduct,
  statusLabel,
  todayIso,
  updateStatusesFor,
} from "./fionaRules";

const FIONA_COLOR = AGENT_INFO.fiona.color;

// The stages and reject reasons, from the backend once loaded
export const useFionaOptions = (store, marketplace) => {
  const [options, setOptions] = useState(DEFAULT_FIONA_OPTIONS);
  useEffect(() => {
    let active = true;
    fetchFionaOptions(store, marketplace)
      .then((data) => {
        if (active && data?.accepted_statuses) setOptions(data);
      })
      .catch(() => {
        // The defaults mirror the backend; nothing else to do
      });
    return () => {
      active = false;
    };
  }, [store, marketplace]);
  return options;
};

// Status pills and follow-up line, for accepted and finished cards.
// withPills false: the row around the card already shows the status.
export const FionaStatus = ({
  fionaCard,
  withPills = true,
  today = todayIso(),
}) => {
  const status = fionaCard?.status;
  if (!isAcceptedStage(status) && status !== STATUS_FINISHED) return null;
  const due = isFollowUpDue(fionaCard, today);
  const followUp = datePart(fionaCard.show_again_at);
  if (!withPills && !followUp) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
      {withPills && (
        <Chip
          label={statusLabel(status)}
          size="small"
          sx={{
            height: 20,
            fontSize: "11px",
            fontWeight: 600,
            color: FIONA_COLOR,
            backgroundColor: `${FIONA_COLOR}14`,
          }}
        />
      )}
      {withPills && due && (
        <Chip
          label="Check-in due"
          size="small"
          color="warning"
          sx={{ height: 20, fontSize: "11px", fontWeight: 600 }}
        />
      )}
      {followUp && (
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          {/* A due row's pill says "Check-in due", so name the stage here */}
          {due && !withPills ? `${statusLabel(status)} · ` : ""}
          {due ? "Check-in was " : "Next check-in "}
          {formatFionaDate(followUp)}
        </Typography>
      )}
    </Box>
  );
};

// What Fiona says she'll do at each stage, when a card is moved to it from
// its steps
const STAGE_NOTES = {
  checking_with_manufacturer: "I’ll check back in 1 month.",
  in_manufacturing:
    "I’ll check back in 3 months, then watch until Amazon remeasures.",
  awaiting_remeasurement:
    "New packaging is at Amazon. I’ll check back in 1 month.",
  [STATUS_FINISHED]:
    "The change is done. It moves to FYI until it’s marked as reviewed.",
};

const OK = "#4caf50";
const NO = "#f44336";

// The production decision buttons (green Accept, red-outlined Reject), with
// the pressed look while their panel is open
const buttonBase = {
  textTransform: "none",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: 1.5,
  borderRadius: "12px",
  boxShadow: "none",
  p: "4px 14px",
  minWidth: 0,
  minHeight: 32,
};
const okButtonSx = {
  ...buttonBase,
  backgroundColor: OK,
  color: "#fff",
  "&:hover": { backgroundColor: "#45a049", boxShadow: "none" },
};
const noButtonSx = { ...buttonBase, borderColor: NO, color: "#e53935" };
const textButtonSx = { ...buttonBase, color: "#6b7280" };
const pressedOkSx = {
  ...buttonBase,
  "&.Mui-disabled": {
    backgroundColor: "#388e3c",
    color: "#fff",
    boxShadow: "inset 0 0 0 2px #1b5e20",
    fontWeight: 600,
  },
};
const pressedNoSx = {
  ...buttonBase,
  "&.Mui-disabled": {
    borderColor: NO,
    color: "#c62828",
    backgroundColor: "#fdecea",
    fontWeight: 600,
  },
};

// The inline panel under the card's buttons, as on the other agents' cards
const Panel = ({ label, children }) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      mt: "16px",
      pt: "16px",
      borderTop: "1px solid #e0e0e0",
      maxWidth: 560,
      position: "relative",
      zIndex: 1,
    }}
  >
    {children}
  </Box>
);

const FollowUpPicker = ({ value, onChange, date, onDateChange, keepDate }) => {
  const today = todayIso();
  const resolved =
    value === "keep"
      ? keepDate
      : value === "date"
      ? date
      : followUpDate(value, today);
  return (
    <Box sx={{ mt: "12px" }}>
      <FormControl fullWidth size="small">
        <InputLabel id="fiona-follow-up">Check back</InputLabel>
        <Select
          labelId="fiona-follow-up"
          label="Check back"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {keepDate && (
            <MenuItem value="keep">Keep {formatFionaDate(keepDate)}</MenuItem>
          )}
          {FOLLOW_UP_OPTIONS.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {value === "date" && (
        <TextField
          type="date"
          size="small"
          fullWidth
          label="Date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: today, "data-testid": "fiona-follow-up-date" }}
          sx={{ mt: "12px" }}
        />
      )}
      {resolved && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: "6px",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          It comes back to Needs decision on {formatFionaDate(resolved)}.
        </Typography>
      )}
    </Box>
  );
};

/**
 * Accept a packaging card into a stage, or update an accepted one
 * (mode "update": the check-in date can be kept).
 */
const FionaStagePanel = ({ mode, decision, options, onCancel, onSubmit }) => {
  const isUpdate = mode === "update";
  const fionaCard = decision.fiona_card || {};
  const cardType = cardTypeOf(decision);
  const statuses = isUpdate
    ? updateStatusesFor(options, cardType)
    : acceptStagesFor(options, cardType);
  const currentFollowUp = datePart(fionaCard.show_again_at);

  const initialStatus = isUpdate ? fionaCard.status : statuses[0];
  const [status, setStatus] = useState(initialStatus);
  // Until the user picks a check-in, it follows the stage's suggestion
  const [followUp, setFollowUp] = useState(
    isUpdate && currentFollowUp ? "keep" : DEFAULT_FOLLOW_UP[initialStatus]
  );
  const [followUpTouched, setFollowUpTouched] = useState(false);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const today = todayIso();
  // Resolved ends the change: no check-in to set
  const isResolved = status === STATUS_FINISHED;
  const showAgainAt =
    isResolved || followUp === "keep"
      ? null
      : followUp === "date"
      ? date
      : followUpDate(followUp, today);
  const dateInvalid =
    !isResolved && followUp === "date" && (!date || date < today);
  const unchanged =
    isUpdate && status === fionaCard.status && followUp === "keep";
  const canSubmit = Boolean(status) && !dateInvalid && !unchanged && !saving;

  const handleStatus = (next) => {
    setStatus(next);
    if (!followUpTouched && !isUpdate && DEFAULT_FOLLOW_UP[next]) {
      setFollowUp(DEFAULT_FOLLOW_UP[next]);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ status, showAgainAt });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Panel label={isUpdate ? "Update status" : "Accept"}>
      <Typography
        sx={{
          fontSize: "13.5px",
          fontWeight: 600,
          color: "#374151",
          mb: "8px",
        }}
      >
        {isUpdate
          ? "Where are we now?"
          : "Where is this packaging change today?"}
      </Typography>
      <RadioGroup
        value={status || ""}
        onChange={(e) => handleStatus(e.target.value)}
      >
        {statuses.map((st) => (
          <FormControlLabel
            key={st}
            value={st}
            control={<Radio size="small" />}
            label={
              <Typography
                sx={{ fontSize: "13.5px", fontWeight: 500, color: "#111827" }}
              >
                {statusLabel(st)}
              </Typography>
            }
            sx={{
              alignItems: "center",
              mx: 0,
              gap: "10px",
              mb: 0,
              p: "8px 10px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: status === st ? "#bbdefb" : "transparent",
              backgroundColor: status === st ? "#f5f9ff" : "transparent",
              "& .MuiRadio-root": { p: 0 },
            }}
          />
        ))}
      </RadioGroup>
      {!isResolved && (
        <FollowUpPicker
          value={followUp}
          onChange={(value) => {
            setFollowUp(value);
            setFollowUpTouched(true);
          }}
          date={date}
          onDateChange={setDate}
          keepDate={isUpdate ? currentFollowUp : null}
        />
      )}
      {!isResolved && followUp === "date" && date && date < today && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: "8px" }}
        >
          The date can't be in the past.
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: "12px" }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: "12px", mt: "12px" }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={okButtonSx}
        >
          {saving ? (
            <CircularProgress size={16} color="inherit" />
          ) : isUpdate ? (
            "Update stage"
          ) : (
            "Confirm"
          )}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={textButtonSx}>
          Cancel
        </Button>
      </Box>
    </Panel>
  );
};

// A price card has no stages: accepting it closes the card
const FionaPricePanel = ({ decision, onCancel, onSubmit }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const priceChange = decision.decision?.price_change;
  const currency = decision.decision?.currency || "USD";
  const money = (value) => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).format(value);
    } catch {
      return `${value} ${currency}`;
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({});
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Panel label="Accept">
      <Typography sx={{ fontSize: "13.5px", color: "#666" }}>
        Confirm the price change
        {priceChange?.current != null &&
          priceChange?.target != null &&
          ` (${money(priceChange.current)} → ${money(priceChange.target)})`}
        ?
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: "4px", fontSize: "12px", color: "#6b7280" }}
      >
        {AGENT_INFO.fiona.name} doesn't change anything on Amazon: change the
        price in Seller Central. Accepting closes the card.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: "12px" }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: "12px", mt: "12px" }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={okButtonSx}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={textButtonSx}>
          Cancel
        </Button>
      </Box>
    </Panel>
  );
};

const FionaRejectPanel = ({ options, onCancel, onSubmit }) => {
  const reasons = options?.reject_reasons || [];
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const reason = reasons.find((r) => r.code === code);
  const noteMissing = Boolean(reason?.note_required) && !note.trim();
  const canSubmit = Boolean(reason) && !noteMissing && !saving;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ reason, note: note.trim() });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Panel label="Reject">
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "12px" }}>
        {reasons.map((r) => {
          const on = r.code === code;
          return (
            <Button
              key={r.code}
              aria-pressed={on}
              onClick={() => setCode(r.code)}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 500,
                height: 32,
                px: "12px",
                borderRadius: "16px",
                border: "1px solid",
                borderColor: on ? "#e53935" : "#c4c4c4",
                backgroundColor: on ? "#fdecea" : "#fff",
                color: on ? "#c62828" : "#374151",
                "&:hover": { backgroundColor: on ? "#fdecea" : "#fafafa" },
              }}
            >
              {r.label}
            </Button>
          );
        })}
      </Box>
      {reason && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: "10px",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          {rejectEffect(reason)}
        </Typography>
      )}
      <TextField
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        size="small"
        placeholder={
          reason?.note_required
            ? "Enter reject reason (required)"
            : "Enter reject reason"
        }
        inputProps={{
          "aria-label": reason?.note_required
            ? "Note (required)"
            : "Note (optional)",
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mt: "12px" }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: "12px", mt: "14px" }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            ...buttonBase,
            backgroundColor: NO,
            color: "#fff",
            "&:hover": { backgroundColor: "#e53935", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "#9e9e9e" },
          }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : "Submit"}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={textButtonSx}>
          Cancel
        </Button>
      </Box>
    </Panel>
  );
};

// In progress: the stages as steps. A step other than the current one can be
// picked (forward or back), then confirmed, which also resets the check-in
const FionaStageSteps = ({ decision, options, allowed, onSubmit }) => {
  const fionaCard = decision.fiona_card || {};
  const statuses = updateStatusesFor(options, cardTypeOf(decision));
  const currentIndex = statuses.indexOf(fionaCard.status);
  const [pick, setPick] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        status: pick,
        // Resolved has no check-in
        showAgainAt:
          pick === STATUS_FINISHED
            ? null
            : followUpDate(DEFAULT_FOLLOW_UP[pick] || "1m"),
      });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const stepSx = (kind) => ({
    fontSize: "11.5px",
    fontWeight: kind === "pick" ? 600 : 500,
    px: "10px",
    py: "3px",
    borderRadius: "12px",
    border: "1px solid",
    lineHeight: 1.4,
    textTransform: "none",
    minWidth: 0,
    ...{
      done: {
        color: FIONA_COLOR,
        borderColor: "#c5cae9",
        backgroundColor: "#f5f6fc",
      },
      now: {
        color: "#fff",
        borderColor: FIONA_COLOR,
        backgroundColor: FIONA_COLOR,
      },
      next: {
        color: FIONA_COLOR,
        borderStyle: "dashed",
        borderColor: "#9fa8da",
        backgroundColor: "#fff",
      },
      pick: {
        color: FIONA_COLOR,
        border: `2px solid ${FIONA_COLOR}`,
        backgroundColor: "#f5f6fc",
      },
      off: {
        color: "#9e9e9e",
        borderColor: "#e0e0e0",
        backgroundColor: "#fff",
      },
    }[kind],
  });

  return (
    <Box sx={{ mt: "10px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
        }}
      >
        {statuses.map((st, i) => {
          const label = statusLabel(st);
          const arrow =
            i > 0 ? (
              <Typography
                component="span"
                aria-hidden="true"
                sx={{ color: "#bdbdbd", fontSize: "12px" }}
              >
                ›
              </Typography>
            ) : null;
          if (i === currentIndex) {
            return (
              <React.Fragment key={st}>
                {arrow}
                <Box component="span" sx={stepSx("now")}>
                  {label}
                </Box>
              </React.Fragment>
            );
          }
          if (!allowed) {
            return (
              <React.Fragment key={st}>
                {arrow}
                <Box
                  component="span"
                  sx={stepSx(i < currentIndex ? "done" : "off")}
                >
                  {label}
                </Box>
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={st}>
              {arrow}
              <Button
                aria-pressed={pick === st}
                title={
                  i < currentIndex
                    ? "Move back to this stage"
                    : "Move forward to this stage"
                }
                onClick={() => setPick(st)}
                sx={stepSx(
                  pick === st ? "pick" : i < currentIndex ? "done" : "next"
                )}
              >
                {label}
              </Button>
            </React.Fragment>
          );
        })}
      </Box>
      {pick && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            mt: "10px",
            pt: "10px",
            borderTop: "1px solid #e0e0e0",
            fontSize: "13px",
            color: "#374151",
          }}
        >
          <span>
            Move {statuses.indexOf(pick) < currentIndex ? "back " : ""}to{" "}
            <b>{statusLabel(pick)}</b>? {STAGE_NOTES[pick]}
          </span>
          <Button
            variant="contained"
            onClick={confirm}
            disabled={saving}
            sx={okButtonSx}
          >
            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Confirm"
            )}
          </Button>
          <Button
            onClick={() => setPick(null)}
            disabled={saving}
            sx={textButtonSx}
          >
            Cancel
          </Button>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: "12px" }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

const FionaActions = ({
  decision,
  store,
  marketplace,
  options,
  onCardChanged,
}) => {
  // The inline panel that's open: accept, acceptPrice, update or reject
  const [dialog, setDialog] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const fionaCard = decision.fiona_card || {};
  const tab = getFionaTab(decision);
  const cardType = cardTypeOf(decision);
  const isOpen = (fionaCard.status || "open") === "open";
  const priceOnly = isClosedOnAccept(options, cardType);
  const allowed = useCanRespondToDecisions(store);
  const track = (event, extra = {}) =>
    trackEvent(event, {
      asin: decision.asin,
      store,
      card_group: decision.decision?.card_group,
      ...extra,
    });

  // A response's cards: the answered one first. Gone cards leave the list.
  const applyCards = (cards, removeScope = "card") => {
    const mine = cards?.[0];
    if (!mine || isGoneStatus(mine.status)) {
      onCardChanged({ remove: removeScope });
    } else {
      onCardChanged({ fionaCard: mergeFionaCard(fionaCard, mine) });
    }
  };

  const handleAccept = async ({ status, showAgainAt }) => {
    const data = await acceptFionaCard(decision, {
      store,
      marketplace,
      status: priceOnly ? undefined : status,
      showAgainAt: priceOnly ? undefined : showAgainAt,
    });
    track("fiona_card_accepted", { status: status || "closed" });
    setDialog(null);
    applyCards(data?.cards);
  };

  const handleReject = async ({ reason, note }) => {
    const data = await rejectFionaCard(decision, {
      store,
      marketplace,
      rejectReason: reason.code,
      rejectNote: note,
    });
    track("fiona_card_rejected", { reason: reason.code });
    setDialog(null);
    applyCards(data?.cards, rejectHidesProduct(reason) ? "product" : "card");
  };

  const handleUpdate = async ({ status, showAgainAt }) => {
    const data = await updateFionaCardStatus(decision, {
      store,
      marketplace,
      status: status !== fionaCard.status ? status : undefined,
      showAgainAt,
    });
    track("fiona_card_status_updated", { status });
    setDialog(null);
    applyCards(data?.card ? [data.card] : []);
  };

  const handleReviewed = async () => {
    setReviewing(true);
    setReviewError(null);
    try {
      const data = await markFionaCardReviewed(decision, {
        store,
        marketplace,
      });
      track("fiona_card_reviewed");
      applyCards(data?.card ? [data.card] : []);
    } catch (e) {
      setReviewError(e.message);
      setReviewing(false);
    }
  };

  const panelOpen = Boolean(dialog);
  const isAccepting = dialog === "accept" || dialog === "acceptPrice";
  // In progress (accepted, check-in still ahead): the stage steps
  const inProgress = isAcceptedStage(fionaCard.status) && tab === "open";
  const checkInDue = isAcceptedStage(fionaCard.status) && tab === "review";

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        {isOpen && (
          <>
            <Button
              variant="contained"
              disabled={!allowed || panelOpen}
              onClick={() => setDialog(priceOnly ? "acceptPrice" : "accept")}
              sx={
                isAccepting
                  ? pressedOkSx
                  : !allowed || panelOpen
                  ? buttonBase
                  : okButtonSx
              }
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              disabled={!allowed || panelOpen}
              onClick={() => setDialog("reject")}
              sx={dialog === "reject" ? pressedNoSx : noButtonSx}
            >
              Reject
            </Button>
          </>
        )}
        {checkInDue && (
          <Button
            variant="contained"
            disabled={!allowed || panelOpen}
            onClick={() => setDialog("update")}
            sx={
              dialog === "update"
                ? pressedOkSx
                : !allowed || panelOpen
                ? buttonBase
                : okButtonSx
            }
          >
            Update status
          </Button>
        )}
        {/* A card being worked on can still be rejected, until it's resolved */}
        {isAcceptedStage(fionaCard.status) && (
          <Button
            variant="outlined"
            disabled={!allowed || panelOpen}
            onClick={() => setDialog("reject")}
            sx={dialog === "reject" ? pressedNoSx : noButtonSx}
          >
            Reject
          </Button>
        )}
        {fionaCard.status === STATUS_FINISHED && (
          <Button
            variant="outlined"
            disabled={!allowed || reviewing}
            onClick={handleReviewed}
            sx={{
              ...buttonBase,
              borderRadius: "8px",
              color: "#1976d2",
              borderColor: "#1976d2",
            }}
          >
            {reviewing ? "Saving…" : "✓ Mark as reviewed"}
          </Button>
        )}
      </Box>

      {inProgress && (
        <FionaStageSteps
          decision={decision}
          options={options}
          allowed={allowed}
          onSubmit={handleUpdate}
        />
      )}

      {reviewError && (
        <Alert severity="error" sx={{ mt: "12px" }}>
          {reviewError}
        </Alert>
      )}
      {!allowed && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: "10px",
            color: "#d32f2f",
            fontStyle: "italic",
          }}
        >
          Only the store owner can act on this card.
        </Typography>
      )}

      {dialog === "accept" && (
        <FionaStagePanel
          mode="accept"
          decision={decision}
          options={options}
          onCancel={() => setDialog(null)}
          onSubmit={handleAccept}
        />
      )}
      {dialog === "acceptPrice" && (
        <FionaPricePanel
          decision={decision}
          onCancel={() => setDialog(null)}
          onSubmit={handleAccept}
        />
      )}
      {dialog === "update" && (
        <FionaStagePanel
          mode="update"
          decision={decision}
          options={options}
          onCancel={() => setDialog(null)}
          onSubmit={handleUpdate}
        />
      )}
      {dialog === "reject" && (
        <FionaRejectPanel
          options={options}
          onCancel={() => setDialog(null)}
          onSubmit={handleReject}
        />
      )}
    </Box>
  );
};

export default FionaActions;
